'use strict';

import React from "react";
import { action, observable, configure, flow, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import {isEmpty} from "@uw-it-sis/lib-react/lib/Utils";
import PlanEvents from "@uw-it-sis/lib-react-myplan/lib/PlanEvents";
import events from "@uw-it-sis/lib-react/lib/Events";
import {States, GENERIC_ERROR_MESSAGE} from "@uw-it-sis/lib-react/lib/AppConstants";
import appStore from "@uw-it-sis/lib-react/lib/AppStore";

import { getSectionCodesFromScheduleOption, prepareScheduleActivityOptions, isSameCourseCodeForActivityId }
    from './ScheduleBuilderUtils';
import {MetricLabels} from "@uw-it-sis/lib-react-myplan/lib/Constants";

class ScheduleBuilderOptionModalStore {
    state = States.initial;

    /**
     * A list of all section codes ( course code + section code ) selected as alternates
     * @type {[]}
     */
    selectedAlternateCodes = [];

    showSendToRegistration = false;

    data = {};

    preparedScheduleItems = [];

    courseCodesWithAlternates = [];

    constructor() {
        makeObservable(this, {
            state: observable,
            selectedAlternateCodes: observable,
            showSendToRegistration: observable,
            data: observable,
            preparedScheduleItems: observable,
            courseCodesWithAlternates: observable,
            mount: action,
            prepareScheduleOptions: action,
            prepareSelections: action,
            toggleShowSendToRegistration: action,
            toggleDisplayAlternates: action,
            chooseAlternatePrimarySection: action,
            chooseAlternateSecondarySection: action,
            resetData: action
        });

        // a mapping of the selection of alternates to primary activity ids
        this.primaryActivityIdToSelectedAlternateCode = {};
        this.schedule = undefined;
        this.analyticsStore = appStore.getAnalyticsStore();
        this.exceptionUtils = appStore.getExceptionUtils();
        this.httpClient = appStore.getHttpClient();
    }

    mount(schedule) {
        this.state = States.pending;

        this.schedule = schedule;

        this.prepareSelections();

        this.prepareScheduleOptions();

        // reset the selection of course codes that had their alternates displayed
        this.data.displayAlternatesPrimaryActivityIds = [];

        this.state = States.done;
    }

    prepareScheduleOptions() {
        this.preparedScheduleItems = prepareScheduleActivityOptions(
            this.schedule.activityOptions,
            this.data.displayAlternatesPrimaryActivityIds,
            this.primaryActivityIdsRequiringAlternateSelection,
            this.selectedAlternateCodes,
            this.showSendToRegistration
        );
    }

    /**
     * Initialize the selection of alternate secondary sections for a schedule option.
     */
    prepareSelections() {
        this.showSendToRegistration = false;

        let tempPrimaries = [];
        let tempSelectedAlternateCodes = [];
        let tempSelectedAlternates = {};
        let tempCourseCodes = [];

        this.schedule.activityOptions.map( item => {

            let primaryCodeWithAlternates = undefined;
            if (!isEmpty(item.alternateActivities)) {
                primaryCodeWithAlternates = `${item.courseCd} ${item.activityCode}`;
            }

            if (primaryCodeWithAlternates !== undefined) {
                tempCourseCodes.push(item.courseCd);
                tempPrimaries.push(item.activityOfferingId);
                tempSelectedAlternateCodes.push(primaryCodeWithAlternates);
                tempSelectedAlternates[item.parentActivityId] = primaryCodeWithAlternates;
            }

            if (!isEmpty(item.secondaryOptions)) {

                let secondaryCodeWithAlternates = undefined;
                item.secondaryOptions.map( secondaryObj => {
                    secondaryObj.activityOptions.map( secondaryActivity => {
                        if (!isEmpty(secondaryActivity.alternateActivities)) {
                            secondaryCodeWithAlternates = `${secondaryActivity.courseCd} ${secondaryActivity.activityCode}`;
                        }
                    });
                });

                if (secondaryCodeWithAlternates !== undefined) {
                    tempCourseCodes.push(item.courseCd);
                    tempPrimaries.push(item.parentActivityId);
                    tempSelectedAlternateCodes.push(secondaryCodeWithAlternates);
                    tempSelectedAlternates[item.parentActivityId] = secondaryCodeWithAlternates;
                }
            }
        });

        this.courseCodesWithAlternates = tempCourseCodes;
        this.primaryActivityIdToSelectedAlternateCode = tempSelectedAlternates;
        this.primaryActivityIdsRequiringAlternateSelection = tempPrimaries;
        this.selectedAlternateCodes = tempSelectedAlternateCodes;
    }

    toggleShowSendToRegistration() {
        this.state = States.pending;

        this.showSendToRegistration = !this.showSendToRegistration;

        this.prepareScheduleOptions();

        this.state = States.done;
    }

    toggleDisplayAlternates(primaryActivityId) {
        this.state = States.pending;

        if(this.data.displayAlternatesPrimaryActivityIds.includes(primaryActivityId)) {
            this.data.displayAlternatesPrimaryActivityIds.remove(primaryActivityId);
        }
        else {
            this.data.displayAlternatesPrimaryActivityIds.push(primaryActivityId);
        }

        this.prepareScheduleOptions();

        this.state = States.done;
    }

    chooseAlternatePrimarySection(activityId, selectedCode) {
        this.state = States.pending;

        // find the root primary activity id by matching the course data in the available primary activity ids requiring alternates
        let rootPrimaryActivityId = this.primaryActivityIdsRequiringAlternateSelection.find(innerId => {
            return isSameCourseCodeForActivityId(innerId, activityId);
        });

        // this index will exist, otherwise the data was not built properly in mount()
        let oldIndex = this.selectedAlternateCodes.indexOf(this.primaryActivityIdToSelectedAlternateCode[rootPrimaryActivityId]);

        this.primaryActivityIdToSelectedAlternateCode[rootPrimaryActivityId] = selectedCode;

        this.selectedAlternateCodes[oldIndex] = selectedCode;

        this.prepareScheduleOptions();

        this.state = States.done;
    }

    chooseAlternateSecondarySection(primaryActivityId, selectedCode) {
        this.state = States.pending;

        // this index will exist, otherwise the data was not built properly in mount()
        let oldIndex = this.selectedAlternateCodes.indexOf(this.primaryActivityIdToSelectedAlternateCode[primaryActivityId]);

        this.primaryActivityIdToSelectedAlternateCode[primaryActivityId] = selectedCode;

        this.selectedAlternateCodes[oldIndex] = selectedCode;

        this.prepareScheduleOptions();

        this.state = States.done;
    }

    reset() {
        this.prepareSelections();
    }

    resetData() {
        if (this.data.submitted) {
            events.emit(PlanEvents.planItemAdded, this.data.eventObject);
        }
        this.data.submitted = false;
        this.data.eventObject = {};
        this.data.errMsg = undefined;
        this.data.displayAlternatesPrimaryActivityIds = [];
        this.preparedScheduleItems = [];
    }

    getPrimaryActivityIdsRequiringAlternateSelection() {
        return this.primaryActivityIdsRequiringAlternateSelection;
    }

    selectForPlan = flow(function* (selectedOption, selectedAlternates, regDate, context, termId) {
        this.state = States.pending;
        /**
         * PLANNING-70 - reset of data here clears out preparedScheduleItems which is used to list sections sent to reg.
         * Removing here as modal exit callback is to reset the data anyway, not sure why reset data is needed here.
         */
        // this.resetData();

        let selectedCodes = getSectionCodesFromScheduleOption(selectedOption, selectedAlternates);
        let postDataArr = [];

        selectedCodes.map(codes =>{
            let postData = {code: codes};
            postDataArr.push(postData);
        });

        this.data.eventObject = {
            code: "SB_ADD_REMOVE",
            data: selectedCodes,
            activityHash: selectedOption.activityHash,
            description: 'Schedule Selection',
            isSection: false,
            atpId: termId,
            eventType: "planItemAdded"
        };

        let url =`api/plan/terms/${termId}`

        try {
            yield this.httpClient.doPatch(url, postDataArr,'selectForPlan');
            if (appStore.isDevMode()) {
                console.log(`ScheduleBuilderOptionModalStore: selectForPlan complete for ${termId}`);
            }
            const labels = this.analyticsStore.label(MetricLabels.quarter, termId).add(MetricLabels.appContextName, context).get();
            this.analyticsStore.addEvent("SB-add-plan",labels, {regDate: regDate});
            this.data.submitted = true;
            this.state = States.done;
        } catch(error) {
            switch(error) {
                case 401:
                    this.exceptionUtils.handleException(error, "ScheduleBuilderOptionModalStore.selectForPlan");
                    break;
                default:
                    this._handlePlanItemError(error);
                    break;
            }
        }
    }.bind(this));

    /**
     * Handle add schedule to plan
     */
    _handlePlanItemError(error) {
        // Can't use exception utils here because we need more granular handling. See if this error
        // is one of ours. If not, use the generic message.
        if (error.exceptionCode) {
            this.errorMessage = error.message;
        } else {
            this.errorMessage = GENERIC_ERROR_MESSAGE
        }

        this.data.errMsg = this.errorMessage;
        this.state = States.error;
    }

}

export default ScheduleBuilderOptionModalStore;